
import { Button } from "@/components/ui/button";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { GalleryCard } from "./passions/GalleryCard";
import { usePassionImages } from "./passions/usePassionImages";
import { galleryData } from "./passions/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState } from "react";

const MeineLeidenschaften = () => {
  const navigate = useNavigate();
  const { imagesActivities, imagesSongs, refetchAll } = usePassionImages();
  const isMobile = useIsMobile();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const handleBack = () => {
    navigate('/', { state: { scrollToId: 'ns6' } });
  };

  const handleImageUpload = () => {
    // This will refresh all image queries after upload
    setTimeout(() => {
      refetchAll();
    }, 1000);
  };

  const getImagesForSection = (section: string) => {
    switch (section) {
      case "passions-activities":
        return imagesActivities || [];
      case "passions-songs":
        return imagesSongs || [];
      default:
        return [];
    }
  };

  // Rearrange the gallery data so songs come first
  const reorderedGalleryData = [galleryData[1], galleryData[0]];

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#243949] to-[#517fa4]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 p-2 h-auto group"
            onClick={handleBack}
          >
            <div className="flex items-center animate-horizontal-bounce">
              <ChevronLeft className="w-4 h-4" />
              <ChevronLeft className="w-4 h-4 -ml-2" />
            </div>
          </Button>
          
          <h2 className="text-lg md:text-xl font-bold text-white text-center uppercase flex-grow">
            Meine Leidenschaften & Hobbies
          </h2>
          
          <div className="w-[40px]"></div>
        </div>

        {/* Text boxes - using the style from NS6 with smaller text */}
        <div className={`mx-auto mb-8 ${isDesktop ? 'max-w-[60%]' : isMobile ? 'w-full' : 'max-w-[70%]'}`}>
          <div className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20 mb-4">
            <p className="text-white text-left text-base md:text-lg">
              Damit du mich ein bisschen kennenlernst, möchte ich dir ein wenig zeigen, für was mein Herz schlägt (außer für Menschen begleiten und Zahlen und Daten analysieren):
            </p>
          </div>
          
          <div className="group relative bg-white/10 backdrop-blur-sm py-2.5 px-4 rounded-lg shadow-lg border border-white/20">
            <p className="text-white text-left text-base md:text-lg">
              Ich liebe Adrenalin, bin oft am Eisbach surfen, liebe die Berge zum Freeriden und Bergsteigen, spiele Gitarre, mache immer noch ein paar Mal im Jahr Gottesdienste und lese für mein Leben gern - am liebsten Thriller am Strand in der Sonne.
            </p>
          </div>
        </div>

        {isMobile ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reorderedGalleryData.map((gallery, index) => (
              <GalleryCard
                key={index}
                gallery={gallery}
                sectionImages={getImagesForSection(gallery.uploadSection)}
                onImageUpload={handleImageUpload}
              />
            ))}
          </div>
        ) : (
          <div className="flex justify-center">
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isDesktop ? 'max-w-[60%]' : 'max-w-[70%]'}`}>
              {reorderedGalleryData.map((gallery, index) => (
                <GalleryCard
                  key={index}
                  gallery={gallery}
                  sectionImages={getImagesForSection(gallery.uploadSection)}
                  maxHeight={isDesktop ? "440px" : undefined}
                  onImageUpload={handleImageUpload}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeineLeidenschaften;
